import { PspContext } from "@/config/contexts";
import {
  ChangeEvent,
  DragEvent,
  useContext,
  useId,
  useRef,
  useState,
} from "react";

type UseFileUploadAreaParams =
  | {
      onChange: (files: File[]) => void;
      singleFile?: false;
    }
  | {
      onChange: (files: File) => void;
      singleFile: true;
    };

export function useFileUploadArea({
  onChange,
  singleFile,
}: UseFileUploadAreaParams) {
  const { lang } = useContext(PspContext);
  const [isDragging, setIsDragging] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>(null);

  function onDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();

    e.dataTransfer.dropEffect = "copy";

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsDragging(true);
  }

  function onDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    timeoutRef.current = setTimeout(() => {
      setIsDragging(false);
    }, 100);
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (singleFile) {
      onChange(e.dataTransfer.files[0]);
    } else {
      onChange([...e.dataTransfer.files]);
    }
  }

  function onFileSelect(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.files !== null && e.target.files.length > 0) {
      if (singleFile) {
        onChange(e.target.files[0]);
      } else {
        onChange([...e.target.files]);
      }
    }
  }

  const inputId = useId();

  return {
    lang,
    onDragOver,
    onDragLeave,
    onDrop,
    isDragging,
    inputId,
    onFileSelect,
  };
}
